import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  TextInput as RNTextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import { ACTION, ITEM, SCREEN, SCREEN_TYPE } from '../../constants/mixpanel';

import {
  useConsumerQuery,
  useInitiateResetEmailMutation,
  useConfirmResetEmailMutation,
  useDoResetEmailMutation,
} from 'apollo/queries/autogenerate/hooks';
import { PinCodeDispatchMethod } from 'apollo/queries/autogenerate/schemas';
import IMAGES from 'shared/constants/images';
import Button from 'shared/modules/common/components/atoms/Button';
import CodeInput from 'shared/modules/common/components/atoms/CodeInput';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import validator, { rules } from 'shared/utils/validator';
import Mixpanel from 'shared/vendor/Mixpanel';

const FIELD_NAME = 'newEmail';
const COUNTDOWN_LENGTH = 30;

type OTPTimerProps = {
  sentToLabel: 'メール' | '自動音声案内';
  onPress: () => void;
};

const OTPTimer = (props: OTPTimerProps) => {
  const [countdownTick, setCountdownTick] = useState(COUNTDOWN_LENGTH);
  const [isOTPRequested, setIsOTPRequested] = useState(false);

  useEffect(() => {
    let timer: number;

    if (countdownTick) {
      // https://github.com/Microsoft/TypeScript/issues/30128#issuecomment-807394387
      timer = +global.setTimeout(() => {
        setCountdownTick(countdownTick - 1);
      }, 1000);
    }

    return () => clearTimeout(timer);
  });

  const onOTPInvoke = async () => {
    setIsOTPRequested(true);
    setCountdownTick(COUNTDOWN_LENGTH);
    props.onPress();
  };

  return (
    <View>
      <Text style={style.textItem}>
        {countdownTick
          ? `${countdownTick}秒以内に届かない場合`
          : '届いていませんか？'}
      </Text>
      <TouchableOpacity
        disabled={!isOTPRequested ? !!countdownTick : false}
        onPress={onOTPInvoke}
      >
        <Text
          style={[
            style.textItem,
            countdownTick && !isOTPRequested
              ? style.textItemDisabled
              : style.textItemPressable,
          ]}
        >
          {isOTPRequested
            ? `${props.sentToLabel}で受け取る`
            : `${props.sentToLabel}で認証コードを受け取る`}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

type FormProps = {
  newEmail: string;
};

type Props = {
  /**
   * Invoked when editing is successful.
   * Automatically invoked in 3 seconds after successful update.
   */
  onSuccess?: () => void;
};

const EditUserEmail = (props: Props) => {
  const { data, refetch } = useConsumerQuery({ fetchPolicy: 'cache-only' });
  const [stepCounter, setStepCounter] = useState(0);
  const [initiateEmailMutation] = useInitiateResetEmailMutation();
  const [confirmResetMutation] = useConfirmResetEmailMutation();
  const [doResetMutation] = useDoResetEmailMutation();
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [smsOtp, setSmsOtp] = useState('');
  const form = useFormWithValidations({ [FIELD_NAME]: '' });
  const codeInputRef = useRef<RNTextInput>(null);

  useEffect(() => {
    codeInputRef.current?.focus();

    const PLEASE_CHECK_EMAIL_EVENT = `${SCREEN.PLEASE_CHECK_EMAIL} - ${ACTION.PAGE_VIEW}`;

    // Record how long the user checks his/her email
    // 新しいメールアドレスに認証コードを送信しました
    if (stepCounter === 3) {
      Mixpanel.timeEvent(PLEASE_CHECK_EMAIL_EVENT);
    }

    if (stepCounter === 4) {
      Mixpanel.trackEvent(PLEASE_CHECK_EMAIL_EVENT, {
        'Screen Type': SCREEN_TYPE.EMAIL_RESET,
      });
    }
  }, [stepCounter]);

  const validateEmailThenSubmit = async () => {
    const { isError, message } = rules.mustNotBeSame(
      form.getValues().newEmail,
      data?.consumer.email || '',
      'メールアドレス'
    );

    if (isError) {
      setErrorMessage(message);

      return;
    }

    const isValid = await form.triggerValidation(FIELD_NAME);

    if (isValid) {
      setErrorMessage('');
      form.clearError(FIELD_NAME);
      submitNewEmail();
    }
  };

  // Sends an OTP code to user's phone
  // So this can be invoked to request a new OTP
  const submitNewEmail = useCallback(
    throttle(
      async () => {
        try {
          const { newEmail } = form.getValues();

          await initiateEmailMutation({
            variables: {
              newEmail,
              pinCodeDispatchMethod:
                stepCounter === 2
                  ? PinCodeDispatchMethod.VOICE
                  : PinCodeDispatchMethod.SMS,
            },
          });

          // We don't want the step counter to change if we request a new OTP
          if (!stepCounter) {
            setStepCounter(1);
          }
        } catch (err) {
          setErrorMessage('入力内容を確認してください');
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    [stepCounter]
  );

  // Sends an OTP code to the email address the user want to become a new email
  // So this can be invoked to request a new OTP
  const submitSMSOTP = async (code?: string) => {
    try {
      await confirmResetMutation({
        variables: {
          otp: code || smsOtp,
        },
      });

      setShowError(false);

      if (code) {
        setSmsOtp(code);
        setStepCounter(3);
      }
    } catch (err) {
      setShowError(true);
    }
  };

  // If the correct OTP is submitted, this permanently updates the user's email address
  // Cannot be used to request a new OTP
  const submitEmailOTP = async (code: string) => {
    try {
      await doResetMutation({
        variables: {
          otp: code,
        },
      });

      Mixpanel.trackEvent(`${SCREEN.EMAIL_RESET_FORM} - ${ACTION.CLICK}`, {
        Item: ITEM.SUBMIT,
      });

      setStepCounter(5);
      refetch();
      setTimeout(() => props.onSuccess?.(), 3000);
    } catch (err) {
      setShowError(true);
    }
  };

  return (
    <View
      style={style.root}
      {...Platform.select({ web: { accessibilityRole: 'form' } })}
    >
      {stepCounter === 0 && (
        <>
          <View style={{ flexGrow: 1 }}>
            <Text>新しいメールアドレスを入力してください。</Text>
            <View style={style.spacer} />
            <TextInput
              label="新しいメールアドレス"
              placeholder="hello@paidy.com"
              value={form.getValues(FIELD_NAME)}
              onChangeText={(text) => form.setValue(FIELD_NAME, text)}
              onFocus={() => {
                setErrorMessage('');
                form.clearError(FIELD_NAME);
              }}
              onBlur={() => {
                setErrorMessage('');
                form.clearError(FIELD_NAME);
                form.triggerValidation(FIELD_NAME);
              }}
              message={
                (form.errors[FIELD_NAME]?.message as string) ||
                errorMessage ||
                ''
              }
              state={
                form.errors[FIELD_NAME]?.message || errorMessage
                  ? 'error'
                  : undefined
              }
              id="edit-user-email-new-email-field"
            />
            <View style={style.spacer} />
          </View>
          <Button
            title="次へ"
            onPress={validateEmailThenSubmit}
            id="edit-user-email-next-button"
          />
        </>
      )}
      {stepCounter === 1 && (
        <>
          <View style={{ flexGrow: 1, justifyContent: 'center' }}>
            <Text style={style.textTitle}>
              ご登録の携帯電話番号に認証コードを送信しました
            </Text>
            <View style={style.spacer} />
            <Text>
              {data?.consumer.phone}
              に送信された4桁の認証コードを確認してください。
            </Text>
            <View style={style.spacer} />
          </View>
          <Button
            title="次へ"
            onPress={() => setStepCounter(2)}
            id="edit-user-email-next-button"
          />
        </>
      )}
      {stepCounter === 2 && (
        <>
          <View style={{ flexGrow: 1 }}>
            <Text style={style.textTitle}>認証コード入力</Text>
            <View style={style.spacer} />
            <Text>
              {data?.consumer.phone}
              に送信された4桁の認証コードを入力してください。
            </Text>
            <View style={style.spacer} />
            <View style={style.otp}>
              <CodeInput
                onFulFill={submitSMSOTP}
                style={style.codeField}
                errorMessage="認証コードが違います"
                showError={showError}
                id="edit-user-email-code-field"
                ref={codeInputRef}
              />
            </View>
          </View>
          <OTPTimer sentToLabel="自動音声案内" onPress={submitNewEmail} />
        </>
      )}
      {stepCounter === 3 && (
        <>
          <View style={{ flexGrow: 1, justifyContent: 'center' }}>
            <Text style={style.textTitle}>
              新しいメールアドレスに認証コードを送信しました
            </Text>
            <View style={style.spacer} />
            <Text>
              {form.getValues(FIELD_NAME)}
              に送信された4桁の認証コードを確認してください。
            </Text>
            <View style={style.spacer} />
          </View>
          <Button
            title="次へ"
            onPress={() => setStepCounter(4)}
            id="edit-user-email-next-button"
          />
        </>
      )}
      {stepCounter === 4 && (
        <>
          <View style={{ flexGrow: 1 }}>
            <Text style={style.textTitle}>認証コード入力</Text>
            <View style={style.spacer} />
            <Text>
              {form.getValues(FIELD_NAME)}
              に送信された4桁の認証コードを入力してください。
            </Text>
            <View style={style.spacer} />
            <View style={style.otp}>
              <CodeInput
                onFulFill={submitEmailOTP}
                style={style.codeField}
                errorMessage="認証コードが違います"
                showError={showError}
                id="edit-user-email-code-field"
                ref={codeInputRef}
              />
            </View>
          </View>
          <OTPTimer sentToLabel="メール" onPress={submitSMSOTP} />
        </>
      )}
      {stepCounter === 5 && (
        <>
          <View style={{ flexGrow: 1, justifyContent: 'center' }}>
            <View style={{ alignItems: 'center' }}>
              <Image
                source={{ uri: IMAGES.success }}
                style={{ width: 256, height: 256 }}
              />
            </View>
            <View style={style.spacer} />
          </View>
          <Text style={[style.textTitle, { textAlign: 'center' }]}>
            メールアドレスの変更が完了しました
          </Text>
        </>
      )}
    </View>
  );
};

const useFormWithValidations = (props: FormProps) => {
  const form = useForm<FormProps>({
    defaultValues: props,
  });

  useEffect(() => {
    form.register(
      {
        name: 'newEmail',
      },
      {
        validate: validator.getRules(['mustBeValidEmail']),
      }
    );
  }, []);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  root: {
    flex: 1,
  },
  otp: {
    alignItems: 'center',
  },
  codeField: {
    width: 288,
  },
  textTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  textItem: {
    textAlign: 'center',
    fontSize: 12,
    marginTop: 8,
  },
  textItemDisabled: {
    color: '#A1AAB0',
  },
  textItemPressable: {
    fontWeight: '500',
  },
  spacer: {
    marginBottom: 24,
  },
});

export default EditUserEmail;
